// Razorpay Payment Service for Hotel A.S.K Grand

export interface BookingDetails {
  name: string;
  email: string;
  phone: string;
  checkIn: string;
  checkOut: string;
  guests: number;
  roomType: 'Single' | 'Royal' | 'Suite';
  specialRequests?: string;
  idProofType: 'aadhar' | 'driving_license' | 'pan_card' | '';
  idProofNumber: string;
}

export interface PaymentDetails {
  amount: number; // in INR
  currency: string;
  receipt: string;
  notes: BookingDetails;
}

// Room pricing (per night in INR)
export const ROOM_PRICES = {
  Single: 900,
  Royal: 1500,
  Suite: 1750,
};

// Calculate total amount based on booking details
export function calculateBookingAmount(
  roomType: 'Single' | 'Royal' | 'Suite',
  checkIn: string,
  checkOut: string,
  guests: number
): number {
  const basePrice = ROOM_PRICES[roomType];
  
  // Calculate number of nights
  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);
  const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
  
  // Ensure at least 1 night
  const totalNights = Math.max(nights, 1);
  
  // Calculate total (base price * nights)
  let total = basePrice * totalNights;
  
  // Add extra guest charges if more than 2 guests
  if (guests > 2) {
    const extraGuests = guests - 2;
    const extraGuestCharge = roomType === 'Suite' ? 500 : roomType === 'Royal' ? 500 : 400;
    total += extraGuestCharge * extraGuests * totalNights;
  }
  
  // Add GST (5%)
  const gst = total * 0.05;
  total += gst;
  
  return Math.round(total);
}

// Initialize Razorpay payment
export function initializeRazorpay(
  bookingDetails: BookingDetails,
  onSuccess: (response: any) => void,
  onFailure: (error: any) => void
) {
  // Calculate total amount
  const amount = calculateBookingAmount(
    bookingDetails.roomType,
    bookingDetails.checkIn,
    bookingDetails.checkOut,
    bookingDetails.guests
  );

  // Razorpay Live Key
  const razorpayKey = 'rzp_live_SWxQ1YSLlcYH0u';

  // Razorpay options
  const options = {
    key: razorpayKey,
    amount: amount * 100, // Razorpay expects amount in paise
    currency: 'INR',
    name: 'Hotel A.S.K Grand',
    description: `${bookingDetails.roomType} Room Booking`,
    image: '/Logo.png', // Your hotel logo
    prefill: {
      name: bookingDetails.name,
      email: bookingDetails.email,
      contact: bookingDetails.phone,
    },
    notes: {
      room_type: bookingDetails.roomType,
      check_in: bookingDetails.checkIn,
      check_out: bookingDetails.checkOut,
      guests: bookingDetails.guests.toString(),
      special_requests: bookingDetails.specialRequests || 'None',
      id_proof_type: bookingDetails.idProofType,
      id_proof_number: bookingDetails.idProofNumber,
    },
    theme: {
      color: '#957F3E', // Gold color matching your brand
    },
    handler: function (response: any) {
      // Payment successful
      onSuccess({
        ...response,
        bookingDetails,
        amount,
      });
    },
    modal: {
      ondismiss: function () {
        onFailure({ message: 'Payment cancelled by user' });
      },
    },
  };

  // Check if Razorpay is loaded
  if (typeof (window as any).Razorpay === 'undefined') {
    console.error('Razorpay SDK not loaded');
    onFailure({ message: 'Payment gateway not available. Please try again.' });
    return;
  }

  // Create Razorpay instance and open
  const razorpay = new (window as any).Razorpay(options);
  
  razorpay.on('payment.failed', function (response: any) {
    onFailure(response.error);
  });
  
  razorpay.open();
}

// Load Razorpay script dynamically
export function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    // Check if already loaded
    if (typeof (window as any).Razorpay !== 'undefined') {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    
    script.onload = () => {
      resolve(true);
    };
    
    script.onerror = () => {
      resolve(false);
    };
    
    document.body.appendChild(script);
  });
}

// Format booking summary for display
export function formatBookingSummary(
  bookingDetails: BookingDetails,
  amount: number
): string {
  const checkInDate = new Date(bookingDetails.checkIn);
  const checkOutDate = new Date(bookingDetails.checkOut);
  const nights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
  
  return `
Booking Summary:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Guest: ${bookingDetails.name}
Room Type: ${bookingDetails.roomType}
Check-in: ${bookingDetails.checkIn}
Check-out: ${bookingDetails.checkOut}
Nights: ${Math.max(nights, 1)}
Guests: ${bookingDetails.guests}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Total Amount: ₹${amount.toLocaleString('en-IN')}
(Including GST)
  `.trim();
}

// Send booking confirmation via WhatsApp
export function sendBookingConfirmation(
  bookingDetails: BookingDetails,
  paymentId: string,
  amount: number
) {
  const message = `🎉 *Booking Confirmed - Hotel A.S.K Grand*

✅ *Payment Successful*
Payment ID: ${paymentId}

👤 *Guest Details:*
Name: ${bookingDetails.name}
Email: ${bookingDetails.email}
Phone: ${bookingDetails.phone}
ID Proof: ${bookingDetails.idProofType.replace('_', ' ').toUpperCase()} - ${bookingDetails.idProofNumber}

🏨 *Booking Details:*
Room Type: ${bookingDetails.roomType}
Check-in: ${bookingDetails.checkIn}
Check-out: ${bookingDetails.checkOut}
Guests: ${bookingDetails.guests}

💰 *Payment Details:*
Amount Paid: ₹${amount.toLocaleString('en-IN')}
Status: CONFIRMED

${bookingDetails.specialRequests ? `📝 *Special Requests:*\n${bookingDetails.specialRequests}\n\n` : ''}Thank you for choosing Hotel A.S.K Grand! We look forward to welcoming you.`;

  const whatsappUrl = `https://wa.me/917799933325?text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, '_blank');
}
